import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  LogOut, Inbox, FileText, Trash2, Mail, Phone, Plus, X, Pencil, CheckCircle2,
  CalendarDays, Clock, LayoutDashboard, UserPlus, MapPin, Award, Link as LinkIcon,
} from "lucide-react";
import Logo from "../components/Logo";
import { api, setToken, getToken } from "../lib/api";
import { useToast } from "../hooks/use-toast";

const STATUSES = ["new", "contacted", "scheduled", "closed"];
const CSTATUSES = ["pending", "approved", "rejected"];
const statusColor = {
  new: "bg-[#F58220]/15 text-[#F58220]",
  contacted: "bg-[#2b8cf0]/15 text-[#2b8cf0]",
  scheduled: "bg-[#16a34a]/15 text-[#16a34a]",
  closed: "bg-gray-200 text-gray-500",
  pending: "bg-[#F58220]/15 text-[#F58220]",
  approved: "bg-[#16a34a]/15 text-[#16a34a]",
  rejected: "bg-red-100 text-red-500",
};

const emptyPost = { title: "", excerpt: "", content: "", category: "Career Tips", cover_image: "", author: "The Career Counsellor" };

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [tab, setTab] = useState("appointments");
  const [appts, setAppts] = useState([]);
  const [posts, setPosts] = useState([]);
  const [counsellors, setCounsellors] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0, scheduled: 0, counsellors: 0 });
  const [modal, setModal] = useState(null); // {mode, data}

  const logout = useCallback(() => { setToken(null); navigate("/admin/login"); }, [navigate]);

  const load = useCallback(async () => {
    try {
      const [a, p, s, c] = await Promise.all([
        api.get("/appointments"),
        api.get("/blog"),
        api.get("/appointments/stats"),
        api.get("/counsellors"),
      ]);
      setAppts(a.data); setPosts(p.data); setStats(s.data); setCounsellors(c.data);
    } catch (e) {
      if (e?.response?.status === 401) logout();
    }
  }, [logout]);

  useEffect(() => {
    if (!getToken()) { navigate("/admin/login"); return; }
    api.get("/admin/verify").then(load).catch(() => logout());
  }, [navigate, load, logout]);

  const setStatus = async (id, status) => {
    await api.patch(`/appointments/${id}`, { status });
    load();
  };
  const delAppt = async (id) => {
    if (!window.confirm("Delete this appointment?")) return;
    await api.delete(`/appointments/${id}`); load();
    toast({ title: "Appointment deleted" });
  };

  const setCStatus = async (id, status) => {
    await api.patch(`/counsellors/${id}`, { status });
    load();
  };
  const delCounsellor = async (id) => {
    if (!window.confirm("Delete this counsellor application?")) return;
    await api.delete(`/counsellors/${id}`); load();
    toast({ title: "Counsellor deleted" });
  };

  const assignAppt = async (id, counsellorId) => {
    const c = counsellors.find((x) => x.id === counsellorId);
    await api.patch(`/appointments/${id}/assign`, {
      counsellor_id: counsellorId || null,
      counsellor_name: c ? c.name : null,
    });
    load();
    toast({ title: counsellorId ? "Counsellor assigned" : "Assignment cleared" });
  };

  const approvedCounsellors = counsellors.filter((c) => c.status === "approved");

  const savePost = async (e) => {
    e.preventDefault();
    const { mode, data } = modal;
    try {
      if (mode === "edit") await api.put(`/blog/${data.id}`, data);
      else await api.post("/blog", data);
      setModal(null); load();
      toast({ title: mode === "edit" ? "Post updated" : "Post published" });
    } catch (e) {
      toast({ title: "Save failed", variant: "destructive" });
    }
  };
  const delPost = async (id) => {
    if (!window.confirm("Delete this post?")) return;
    await api.delete(`/blog/${id}`); load();
    toast({ title: "Post deleted" });
  };

  const fmt = (d) => new Date(d).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });

  return (
    <div className="min-h-screen bg-[#f6f8fc]">
      <header className="bg-white shadow-sm sticky top-0 z-40">
        <div className="max-w-[1240px] mx-auto px-5 h-[70px] flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Logo height={40} />
            <span className="hidden sm:inline-flex items-center gap-1.5 text-sm font-head font-semibold text-[#16244F] border-l pl-3">
              <LayoutDashboard size={16} /> Admin
            </span>
          </div>
          <button onClick={logout} className="inline-flex items-center gap-1.5 text-[#16244F] hover:text-[#F58220] font-head text-sm font-semibold">
            <LogOut size={16} /> Logout
          </button>
        </div>
      </header>

      <div className="max-w-[1240px] mx-auto px-5 py-8">
        {/* stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {[
            { l: "Total Bookings", v: stats.total, icon: Inbox, c: "#16244F" },
            { l: "New Requests", v: stats.new, icon: Clock, c: "#F58220" },
            { l: "Scheduled", v: stats.scheduled, icon: CalendarDays, c: "#16a34a" },
            { l: "Counsellors", v: stats.counsellors, icon: UserPlus, c: "#2b8cf0" },
          ].map((s) => (
            <div key={s.l} className="bg-white rounded-2xl p-4 md:p-5 flex items-center gap-3 cc-shadow-card">
              <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${s.c}15`, color: s.c }}>
                <s.icon size={22} />
              </div>
              <div className="min-w-0">
                <div className="font-head text-xl font-bold text-[#16244F] truncate">{s.v}</div>
                <div className="text-xs text-[#8a90a0]">{s.l}</div>
              </div>
            </div>
          ))}
        </div>

        {/* tabs */}
        <div className="flex gap-2 mb-6 flex-wrap">
          {[
            { k: "appointments", l: "Appointments", icon: Inbox },
            { k: "counsellors", l: "Counsellors", icon: UserPlus },
            { k: "blog", l: "Blog Posts", icon: FileText },
          ].map((t) => (
            <button
              key={t.k}
              onClick={() => setTab(t.k)}
              className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-lg font-head font-semibold text-sm transition-colors ${
                tab === t.k ? "bg-[#16244F] text-white" : "bg-white text-[#16244F] hover:bg-gray-50"
              }`}
            >
              <t.icon size={16} /> {t.l}
            </button>
          ))}
        </div>

        {tab === "counsellors" ? (
          <div className="bg-white rounded-2xl cc-shadow-card overflow-hidden">
            {counsellors.length === 0 ? (
              <div className="p-12 text-center text-[#8a90a0]">No counsellor applications yet.</div>
            ) : (
              <div className="divide-y divide-gray-100">
                {counsellors.map((c) => (
                  <div key={c.id} className="p-5 flex flex-col md:flex-row md:items-start gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 flex-wrap">
                        <span className="font-head font-semibold text-[#16244F]">{c.name}</span>
                        <span className={`text-xs px-2.5 py-1 rounded-full font-semibold ${statusColor[c.status]}`}>{c.status}</span>
                        <span className="text-xs text-[#8a90a0]">{fmt(c.created_at)}</span>
                      </div>
                      <div className="flex items-center gap-4 mt-2 text-sm text-[#5a6274] flex-wrap">
                        <a href={`mailto:${c.email}`} className="inline-flex items-center gap-1 hover:text-[#2b8cf0]"><Mail size={14} /> {c.email}</a>
                        {c.phone && <a href={`tel:${c.phone}`} className="inline-flex items-center gap-1 hover:text-[#2b8cf0]"><Phone size={14} /> {c.phone}</a>}
                        {c.city && <span className="inline-flex items-center gap-1"><MapPin size={14} /> {c.city}</span>}
                      </div>
                      <div className="flex items-center gap-4 mt-2 text-sm text-[#5a6274] flex-wrap">
                        <span className="inline-flex items-center gap-1"><Award size={14} /> {c.qualification}{c.experience ? ` · ${c.experience} yrs` : ""}</span>
                        {c.specialization && <span className="inline-flex items-center gap-1 text-[#2b8cf0] font-semibold">{c.specialization}</span>}
                        {c.linkedin && <a href={c.linkedin} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 hover:text-[#2b8cf0]"><LinkIcon size={14} /> Profile</a>}
                      </div>
                      {c.message && <p className="text-sm text-[#5a6274] mt-2 bg-[#f6f8fc] rounded-lg p-3">{c.message}</p>}
                    </div>
                    <div className="flex items-center gap-2">
                      <select
                        value={c.status}
                        onChange={(e) => setCStatus(c.id, e.target.value)}
                        className="text-sm border border-gray-200 rounded-lg px-3 py-2 outline-none focus:border-[#2b8cf0]"
                      >
                        {CSTATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                      <button onClick={() => delCounsellor(c.id)} className="p-2 text-gray-400 hover:text-red-500 transition-colors"><Trash2 size={18} /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : tab === "appointments" ? (
          <div className="bg-white rounded-2xl cc-shadow-card overflow-hidden">
            {appts.length === 0 ? (
              <div className="p-12 text-center text-[#8a90a0]">No appointments yet.</div>
            ) : (
              <div className="divide-y divide-gray-100">
                {appts.map((a) => (
                  <div key={a.id} className="p-5 flex flex-col md:flex-row md:items-center gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 flex-wrap">
                        <span className="font-head font-semibold text-[#16244F]">{a.name}</span>
                        <span className={`text-xs px-2.5 py-1 rounded-full font-semibold ${statusColor[a.status]}`}>{a.status}</span>
                        <span className="text-xs text-[#8a90a0]">{fmt(a.created_at)}</span>
                      </div>
                      <div className="flex items-center gap-4 mt-2 text-sm text-[#5a6274] flex-wrap">
                        <a href={`mailto:${a.email}`} className="inline-flex items-center gap-1 hover:text-[#2b8cf0]"><Mail size={14} /> {a.email}</a>
                        {a.phone && <a href={`tel:${a.phone}`} className="inline-flex items-center gap-1 hover:text-[#2b8cf0]"><Phone size={14} /> {a.phone}</a>}
                      </div>
                      {a.message && <p className="text-sm text-[#5a6274] mt-2 bg-[#f6f8fc] rounded-lg p-3">{a.message}</p>}
                      {a.quiz_result && <p className="text-xs text-[#2b8cf0] mt-2 font-semibold">Quiz result: {a.quiz_result}</p>}
                      {a.assigned_counsellor_name && <p className="text-xs text-[#16a34a] mt-2 font-semibold">Assigned to: {a.assigned_counsellor_name}</p>}
                    </div>
                    <div className="flex flex-col sm:items-end gap-2">
                      <div className="flex items-center gap-2">
                        <select
                          value={a.status}
                          onChange={(e) => setStatus(a.id, e.target.value)}
                          className="text-sm border border-gray-200 rounded-lg px-3 py-2 outline-none focus:border-[#2b8cf0]"
                        >
                          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                        <button onClick={() => delAppt(a.id)} className="p-2 text-gray-400 hover:text-red-500 transition-colors"><Trash2 size={18} /></button>
                      </div>
                      <select
                        value={a.assigned_counsellor_id || ""}
                        onChange={(e) => assignAppt(a.id, e.target.value)}
                        className="text-sm border border-gray-200 rounded-lg px-3 py-2 outline-none focus:border-[#2b8cf0] min-w-[180px]"
                      >
                        <option value="">Assign counsellor...</option>
                        {approvedCounsellors.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                      </select>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div>
            <div className="flex justify-end mb-4">
              <button onClick={() => setModal({ mode: "create", data: { ...emptyPost } })} className="inline-flex items-center gap-2 bg-[#F58220] hover:bg-[#e0730f] text-white font-head font-semibold px-5 py-2.5 rounded-lg transition-colors">
                <Plus size={16} /> New Post
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {posts.map((p) => (
                <div key={p.id} className="bg-white rounded-2xl cc-shadow-card overflow-hidden flex">
                  {p.cover_image && <img src={p.cover_image} alt={p.title} className="w-28 h-auto object-cover" />}
                  <div className="p-4 flex-1">
                    <span className="text-xs text-[#2b8cf0] font-semibold">{p.category}</span>
                    <h3 className="font-head font-semibold text-[#16244F] leading-snug mt-1">{p.title}</h3>
                    <p className="text-xs text-[#8a90a0] mt-1 line-clamp-2">{p.excerpt}</p>
                    <div className="flex gap-2 mt-3">
                      <button onClick={() => setModal({ mode: "edit", data: { ...p } })} className="inline-flex items-center gap-1 text-xs font-semibold text-[#16244F] hover:text-[#2b8cf0]"><Pencil size={13} /> Edit</button>
                      <button onClick={() => delPost(p.id)} className="inline-flex items-center gap-1 text-xs font-semibold text-gray-400 hover:text-red-500"><Trash2 size={13} /> Delete</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* blog modal */}
      {modal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 overflow-y-auto">
          <form onSubmit={savePost} className="bg-white rounded-2xl w-full max-w-2xl p-7 my-8">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-head text-xl font-bold text-[#16244F]">{modal.mode === "edit" ? "Edit Post" : "New Post"}</h3>
              <button type="button" onClick={() => setModal(null)} className="text-gray-400 hover:text-gray-600"><X size={22} /></button>
            </div>
            <div className="space-y-4">
              <input required placeholder="Title" value={modal.data.title} onChange={(e) => setModal({ ...modal, data: { ...modal.data, title: e.target.value } })} className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-[#2b8cf0] outline-none" />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <input placeholder="Category" value={modal.data.category} onChange={(e) => setModal({ ...modal, data: { ...modal.data, category: e.target.value } })} className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-[#2b8cf0] outline-none" />
                <input placeholder="Author" value={modal.data.author} onChange={(e) => setModal({ ...modal, data: { ...modal.data, author: e.target.value } })} className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-[#2b8cf0] outline-none" />
              </div>
              <input placeholder="Cover image URL" value={modal.data.cover_image} onChange={(e) => setModal({ ...modal, data: { ...modal.data, cover_image: e.target.value } })} className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-[#2b8cf0] outline-none" />
              <textarea required placeholder="Short excerpt" rows={2} value={modal.data.excerpt} onChange={(e) => setModal({ ...modal, data: { ...modal.data, excerpt: e.target.value } })} className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-[#2b8cf0] outline-none resize-none" />
              <textarea required placeholder="Full content (use blank lines between paragraphs)" rows={8} value={modal.data.content} onChange={(e) => setModal({ ...modal, data: { ...modal.data, content: e.target.value } })} className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-[#2b8cf0] outline-none resize-none" />
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button type="button" onClick={() => setModal(null)} className="px-5 py-2.5 rounded-lg font-head font-semibold text-[#5a6274] hover:bg-gray-100">Cancel</button>
              <button type="submit" className="inline-flex items-center gap-2 bg-[#F58220] hover:bg-[#e0730f] text-white font-head font-semibold px-5 py-2.5 rounded-lg"><CheckCircle2 size={16} /> {modal.mode === "edit" ? "Update" : "Publish"}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
